import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  CUSTOMER_DISPLAY_MAIN_TAB_CLOSING_STORAGE_KEY,
  CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY,
  CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY,
  clearCustomerDisplayOwnerTabId,
  createCustomerDisplayChannel,
  getCustomerDisplayMessageOwnerTabId,
  getCustomerDisplayOwnerTabId,
  isCustomerDisplaySourceMessage,
  parseCustomerDisplayOwnerTabStatus,
  parseCustomerDisplayMainTabClosingEvent,
  postCustomerDisplayTargetedMessage,
} from 'Views/CustomerDisplay/utils/customerDisplayChannel'
import { positionCurrentCustomerDisplayWindow } from 'Views/CustomerDisplay/utils/customerDisplayWindow'
import { useGracePeriodTimeout } from 'Views/CustomerDisplay/hook/useGracePeriodTimeout'
import { getCustomerDisplayMainAppPresenceState } from 'Views/CustomerDisplay/utils/customerDisplayPresence'
import { createCustomerDisplayId } from 'Views/CustomerDisplay/utils/customerDisplayId'

const CustomerDisplayContext = createContext()
const MAIN_APP_HEARTBEAT_TIMEOUT_MS = 8000
const MAIN_APP_HEARTBEAT_CHECK_INTERVAL_MS = 3000
const MAIN_APP_TAB_CLOSE_GRACE_PERIOD_MS = 5000
const MAIN_APP_PRESENCE_CHECK_INTERVAL_MS = 2000
const MAIN_APP_OWNER_RECOVERY_TIMEOUT_MS = 5000
const MAIN_APP_OWNER_RECOVERY_RETRY_MS = 2000
const MAIN_APP_ORPHAN_DETECTION_GRACE_MS = 15000
const CUSTOMER_DISPLAY_POSITION_RETRY_MS = 1000
const CUSTOMER_DISPLAY_POSITION_MAX_ATTEMPTS = 5
const CUSTOMER_DISPLAY_ROUTES = {
  idle: '/customer_display',
  cart: '/customer_display/cart',
  quote: '/customer_display/quote',
  search: '/customer_display/search',
}

const getQuoteUnitPrice = (product, showDiscount) => {
  const basePrice = product?.BasePrice
  const price = product?.Price ?? basePrice ?? 0

  if (basePrice == null) return price

  return !showDiscount || price > basePrice ? price : basePrice
}

const closeCurrentWindowSafely = reason => {
  try {
    window.close()
  } catch (error) {
    console.warn(`No se pudo cerrar Customer Display ${reason}:`, error)
  }
}

const createOwnerRecoveryRequestId = createCustomerDisplayId

export const useCustomerDisplay = () => {
  const context = useContext(CustomerDisplayContext)
  if (!context) {
    throw new Error(
      'useCustomerDisplay must be used within CustomerDisplayProvider'
    )
  }
  return context
}

export const CustomerDisplayProvider = ({ children }) => {
  const history = useHistory()
  const location = useLocation()
  const { CompanyId, BranchOfficeId } = useSelector(state => state.company)

  // Data states
  const [listProducts, setListProducts] = useState([])
  const [isLoadingProductSearch, setIsLoadingProductSearch] = useState(true)
  const [productsCart, setProductsCart] = useState([])
  const [productDetail, setProductDetail] = useState(null)
  const [quoteData, setQuoteData] = useState({})
  const [isLoadingDetailQuote, setIsLoadingDetailQuote] = useState(false)
  const [subtotal, setSubtotal] = useState(0)
  const [itbms, setItbms] = useState(0)
  const [total, setTotal] = useState(0)
  const [company, setCompany] = useState(CompanyId)
  const [branchOffice, setBranchOffice] = useState(BranchOfficeId)
  const [isChangeCompany, setIsChangeCompany] = useState(false)
  const [showDiscount, setShowDiscount] = useState(true)
  const [priceChange, setPriceChange] = useState(false)
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false)
  const [isSleepMode, setIsSleepMode] = useState(false)

  const channelRef = useRef(null)
  const isClosingProductDetailRef = useRef(false)
  const isProductDetailOpenRef = useRef(isProductDetailOpen)
  const lastActivitySentRef = useRef(0)
  const prevProductsCartLengthRef = useRef(0)
  const mainRouteIsSupportedRef = useRef(true)
  const windowCloseNotifiedRef = useRef(false)
  const lastMainAppHeartbeatRef = useRef(null)
  const activeMainAppTabIdRef = useRef(getCustomerDisplayOwnerTabId())
  const mainAppTabHiddenRef = useRef(false)
  const mainAppPresenceMissingRef = useRef(false)
  const mainAppOwnerRecoveryRef = useRef(null)
  const orphanCloseScheduledRef = useRef(false)
  const contextInitializedAtRef = useRef(Date.now())
  const {
    clearGracePeriodTimeout: clearMainAppTabCloseTimeout,
    scheduleGracePeriodTimeout: scheduleMainAppTabCloseTimeout,
  } = useGracePeriodTimeout()

  // Initialize BroadcastChannel immediately
  if (!channelRef.current) {
    channelRef.current = createCustomerDisplayChannel()
  }

  const postMessageToMainApp = useCallback(message => {
    postCustomerDisplayTargetedMessage(
      channelRef.current,
      message,
      activeMainAppTabIdRef.current
    )
  }, [])

  const postMessageToAllMainApps = useCallback(message => {
    postCustomerDisplayTargetedMessage(channelRef.current, message)
  }, [])

  const clearMainAppOwnerRecovery = useCallback(() => {
    mainAppOwnerRecoveryRef.current = null
  }, [])

  const setActiveMainAppTab = useCallback(
    tabId => {
      if (!tabId) return false

      const ownerChanged = activeMainAppTabIdRef.current !== tabId
      const ownerRecovered =
        mainAppOwnerRecoveryRef.current?.ownerTabId === tabId
      activeMainAppTabIdRef.current = tabId

      if (ownerChanged || ownerRecovered) {
        mainAppTabHiddenRef.current = false
        mainAppPresenceMissingRef.current = false
        orphanCloseScheduledRef.current = false
        clearMainAppOwnerRecovery()
      }

      return true
    },
    [clearMainAppOwnerRecovery]
  )

  const scheduleMainAppTabClose = useCallback(
    (
      reason,
      delay = MAIN_APP_TAB_CLOSE_GRACE_PERIOD_MS,
      { isOrphanClose = false } = {}
    ) => {
      const expectedOwnerTabId = activeMainAppTabIdRef.current
      if (!expectedOwnerTabId) return

      if (orphanCloseScheduledRef.current && !isOrphanClose) {
        return
      }

      if (isOrphanClose) {
        orphanCloseScheduledRef.current = true
      }

      mainAppTabHiddenRef.current = false
      scheduleMainAppTabCloseTimeout(() => {
        if (activeMainAppTabIdRef.current !== expectedOwnerTabId) return

        clearCustomerDisplayOwnerTabId(expectedOwnerTabId)
        closeCurrentWindowSafely(reason)
      }, delay)
    },
    [scheduleMainAppTabCloseTimeout]
  )

  const requestMainAppOwnerRecovery = useCallback(
    (ownerTabId, reason = 'sin respuesta de la app principal') => {
      if (!ownerTabId || activeMainAppTabIdRef.current !== ownerTabId) {
        return false
      }

      const now = Date.now()
      let recovery = mainAppOwnerRecoveryRef.current

      if (!recovery || recovery.ownerTabId !== ownerTabId) {
        recovery = {
          ownerTabId,
          requestId: null,
          lastBroadcastAt: null,
          candidateTabId: null,
          candidateSelectedAt: null,
        }
        mainAppOwnerRecoveryRef.current = recovery
        mainAppPresenceMissingRef.current = false
        scheduleMainAppTabClose(reason, MAIN_APP_OWNER_RECOVERY_TIMEOUT_MS)
      }

      if (
        recovery.candidateTabId &&
        now - recovery.candidateSelectedAt < MAIN_APP_OWNER_RECOVERY_RETRY_MS
      ) {
        return true
      }
      if (
        recovery.lastBroadcastAt &&
        now - recovery.lastBroadcastAt < MAIN_APP_OWNER_RECOVERY_RETRY_MS
      ) {
        return true
      }

      const requestId = createOwnerRecoveryRequestId()
      mainAppOwnerRecoveryRef.current = {
        ...recovery,
        requestId,
        lastBroadcastAt: now,
        candidateTabId: null,
        candidateSelectedAt: null,
      }

      postMessageToAllMainApps({
        customerDisplayOwnershipRecoveryRequest: true,
        previousOwnerTabId: ownerTabId,
        requestId,
      })

      return true
    },
    [postMessageToAllMainApps, scheduleMainAppTabClose]
  )

  const grantMainAppOwnerRecovery = useCallback(data => {
    const recovery = mainAppOwnerRecoveryRef.current
    const candidateTabId = data.tabId

    if (
      !recovery ||
      !candidateTabId ||
      recovery.requestId !== data.requestId ||
      recovery.ownerTabId !== data.previousOwnerTabId ||
      (recovery.candidateTabId && recovery.candidateTabId !== candidateTabId)
    ) {
      return false
    }

    recovery.candidateTabId = candidateTabId
    recovery.candidateSelectedAt = Date.now()
    postCustomerDisplayTargetedMessage(
      channelRef.current,
      {
        customerDisplayOwnershipGranted: true,
        connectionIsConfirmed: true,
        previousOwnerTabId: recovery.ownerTabId,
        requestId: recovery.requestId,
      },
      candidateTabId
    )

    return true
  }, [])

  useEffect(() => {
    isProductDetailOpenRef.current = isProductDetailOpen
  }, [isProductDetailOpen])

  const handleSetIsProductDetailOpen = useCallback(value => {
    if (value === false && isProductDetailOpenRef.current) {
      isClosingProductDetailRef.current = true
    }

    isProductDetailOpenRef.current = value
    setIsProductDetailOpen(value)
  }, [])

  useEffect(() => {
    let disposed = false
    let retryTimeoutId = null
    let attempts = 0

    const positionWindow = async () => {
      attempts += 1
      await positionCurrentCustomerDisplayWindow()

      if (disposed || attempts >= CUSTOMER_DISPLAY_POSITION_MAX_ATTEMPTS) {
        return
      }

      retryTimeoutId = globalThis.setTimeout(
        positionWindow,
        CUSTOMER_DISPLAY_POSITION_RETRY_MS
      )
    }

    positionWindow()

    return () => {
      disposed = true
      if (retryTimeoutId) {
        globalThis.clearTimeout(retryTimeoutId)
      }
    }
  }, [])

  useEffect(() => {
    let disposed = false
    let checkInProgress = false

    const checkMainAppPresence = async () => {
      if (checkInProgress) return

      const checkedOwnerTabId = activeMainAppTabIdRef.current
      if (!checkedOwnerTabId) return

      checkInProgress = true
      const presenceState =
        await getCustomerDisplayMainAppPresenceState(checkedOwnerTabId)
      checkInProgress = false

      if (disposed || activeMainAppTabIdRef.current !== checkedOwnerTabId) {
        return
      }

      if (presenceState === null) {
        const recoveringOwnerTabId = mainAppOwnerRecoveryRef.current?.ownerTabId
        if (recoveringOwnerTabId) {
          requestMainAppOwnerRecovery(recoveringOwnerTabId)
          return
        }

        const timeSinceInit = Date.now() - contextInitializedAtRef.current
        const hasReceivedHeartbeat = lastMainAppHeartbeatRef.current !== null
        if (
          !hasReceivedHeartbeat &&
          timeSinceInit > MAIN_APP_ORPHAN_DETECTION_GRACE_MS
        ) {
          scheduleMainAppTabClose(
            'sin respuesta inicial de la app principal',
            MAIN_APP_TAB_CLOSE_GRACE_PERIOD_MS,
            { isOrphanClose: true }
          )
        }
        return
      }

      if (presenceState.ownerIsPresent) {
        mainAppPresenceMissingRef.current = false
        orphanCloseScheduledRef.current = false
        clearMainAppOwnerRecovery()
        clearMainAppTabCloseTimeout()
        return
      }

      if (presenceState.anotherTabIsPresent) {
        requestMainAppOwnerRecovery(checkedOwnerTabId)
        return
      }

      if (mainAppOwnerRecoveryRef.current?.ownerTabId === checkedOwnerTabId) {
        return
      }

      clearMainAppOwnerRecovery()

      if (activeMainAppTabIdRef.current && !mainAppPresenceMissingRef.current) {
        mainAppPresenceMissingRef.current = true
        scheduleMainAppTabClose(
          'sin pestañas de la app principal',
          MAIN_APP_TAB_CLOSE_GRACE_PERIOD_MS,
          { isOrphanClose: true }
        )
      }
    }

    checkMainAppPresence()
    const presenceInterval = setInterval(
      checkMainAppPresence,
      MAIN_APP_PRESENCE_CHECK_INTERVAL_MS
    )

    return () => {
      disposed = true
      clearInterval(presenceInterval)
    }
  }, [
    clearMainAppTabCloseTimeout,
    clearMainAppOwnerRecovery,
    requestMainAppOwnerRecovery,
    scheduleMainAppTabClose,
  ])

  useEffect(() => {
    const notifyCustomerDisplayWindowClosed = event => {
      if (event?.persisted) return
      if (windowCloseNotifiedRef.current) return

      windowCloseNotifiedRef.current = true
      postMessageToMainApp({ customerDisplayWindowClosed: true })
    }

    window.addEventListener('pagehide', notifyCustomerDisplayWindowClosed)
    window.addEventListener('beforeunload', notifyCustomerDisplayWindowClosed)

    return () => {
      window.removeEventListener('pagehide', notifyCustomerDisplayWindowClosed)
      window.removeEventListener(
        'beforeunload',
        notifyCustomerDisplayWindowClosed
      )
    }
  }, [postMessageToMainApp])

  useEffect(() => {
    const validateMainAppHeartbeat = () => {
      if (mainAppTabHiddenRef.current) return
      if (mainAppOwnerRecoveryRef.current) return
      if (orphanCloseScheduledRef.current) return

      const now = Date.now()
      const lastHeartbeat = lastMainAppHeartbeatRef.current
      const timeSinceInit = now - contextInitializedAtRef.current

      if (!lastHeartbeat) {
        if (timeSinceInit > MAIN_APP_ORPHAN_DETECTION_GRACE_MS) {
          scheduleMainAppTabClose(
            'sin respuesta inicial de la app principal',
            MAIN_APP_TAB_CLOSE_GRACE_PERIOD_MS,
            { isOrphanClose: true }
          )
        }
        return
      }

      const heartbeatAge = now - lastHeartbeat
      if (heartbeatAge > MAIN_APP_HEARTBEAT_TIMEOUT_MS) {
        requestMainAppOwnerRecovery(
          activeMainAppTabIdRef.current,
          'sin respuesta de la app principal'
        )
      }
    }

    const heartbeatInterval = setInterval(
      validateMainAppHeartbeat,
      MAIN_APP_HEARTBEAT_CHECK_INTERVAL_MS
    )

    return () => {
      clearInterval(heartbeatInterval)
    }
  }, [requestMainAppOwnerRecovery, scheduleMainAppTabClose])

  // Navigate to cart when a product is added from search or when cart goes from empty to non-empty
  useEffect(() => {
    const prevLength = prevProductsCartLengthRef.current
    prevProductsCartLengthRef.current = productsCart.length

    const productWasAdded = productsCart.length > prevLength
    const onSearchPage = location.pathname === CUSTOMER_DISPLAY_ROUTES.search

    if (
      productWasAdded &&
      (prevLength === 0 || onSearchPage) &&
      !isProductDetailOpen
    ) {
      history.push(CUSTOMER_DISPLAY_ROUTES.cart)
    }
  }, [productsCart, isProductDetailOpen, history, location.pathname])

  // Navigate to background when cart becomes empty (only from cart view)
  useEffect(() => {
    if (
      location.pathname === CUSTOMER_DISPLAY_ROUTES.cart &&
      productsCart.length === 0 &&
      !isProductDetailOpen
    ) {
      history.push(CUSTOMER_DISPLAY_ROUTES.idle)
    }
  }, [productsCart, location.pathname, history, isProductDetailOpen])

  // Handle product detail closing to prevent cart data flicker and navigate to cart
  useEffect(() => {
    if (!isProductDetailOpen && isClosingProductDetailRef.current) {
      // Navigate to cart if there are products and we're on search page
      if (
        productsCart.length > 0 &&
        location.pathname === CUSTOMER_DISPLAY_ROUTES.search
      ) {
        history.push(CUSTOMER_DISPLAY_ROUTES.cart)
      }

      // Reset the flag after a short delay
      const timeout = setTimeout(() => {
        isClosingProductDetailRef.current = false
      }, 300)
      return () => clearTimeout(timeout)
    }
  }, [isProductDetailOpen, productsCart.length, location.pathname, history])

  // Notify main app of user activity to reset inactivity timer
  useEffect(() => {
    const notifyActivity = () => {
      const now = Date.now()
      // Throttle: only send message once per second
      if (now - lastActivitySentRef.current > 1000) {
        if (channelRef.current) {
          postMessageToMainApp({ userActivity: true })
          lastActivitySentRef.current = now
        }
      }
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, notifyActivity))

    return () => {
      events.forEach(e => window.removeEventListener(e, notifyActivity))
    }
  }, [postMessageToMainApp])

  const resetCustomerDisplayToIdle = useCallback(
    ({ enterSleepMode } = {}) => {
      setListProducts([])
      setIsLoadingProductSearch(false)
      setProductsCart([])
      setProductDetail(null)
      setQuoteData({})
      setIsLoadingDetailQuote(false)
      setSubtotal(0)
      setItbms(0)
      setTotal(0)
      setShowDiscount(true)
      setPriceChange(false)
      setIsProductDetailOpen(false)
      setIsSleepMode(Boolean(enterSleepMode))
      isClosingProductDetailRef.current = false
      prevProductsCartLengthRef.current = 0
      history.replace(CUSTOMER_DISPLAY_ROUTES.idle)
    },
    [history]
  )

  useEffect(() => {
    const handleCustomerDisplayStorage = event => {
      if (event.key === CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY) {
        const nextOwnerTabId = event.newValue

        if (
          !nextOwnerTabId ||
          nextOwnerTabId === activeMainAppTabIdRef.current
        ) {
          return
        }

        if (setActiveMainAppTab(nextOwnerTabId)) {
          clearMainAppTabCloseTimeout()
          lastMainAppHeartbeatRef.current = Date.now()
          resetCustomerDisplayToIdle()
          postMessageToMainApp({ requestCustomerDisplaySync: true })
        }

        return
      }

      if (event.key === CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY) {
        const ownerStatus = parseCustomerDisplayOwnerTabStatus(event.newValue)

        if (ownerStatus?.tabId !== activeMainAppTabIdRef.current) return
        if (ownerStatus.isClosing) {
          requestMainAppOwnerRecovery(
            ownerStatus.tabId,
            'por cierre de la app principal'
          )
          return
        }

        clearMainAppTabCloseTimeout()
        clearMainAppOwnerRecovery()
        orphanCloseScheduledRef.current = false
        lastMainAppHeartbeatRef.current = Date.now()
        mainAppTabHiddenRef.current = ownerStatus.isHidden
        return
      }

      if (event.key !== CUSTOMER_DISPLAY_MAIN_TAB_CLOSING_STORAGE_KEY) return

      const closingEvent = parseCustomerDisplayMainTabClosingEvent(
        event.newValue
      )

      if (closingEvent?.tabId !== activeMainAppTabIdRef.current) return

      requestMainAppOwnerRecovery(
        closingEvent.tabId,
        'por cierre de la app principal'
      )
    }

    window.addEventListener('storage', handleCustomerDisplayStorage)

    return () => {
      window.removeEventListener('storage', handleCustomerDisplayStorage)
    }
  }, [
    clearMainAppTabCloseTimeout,
    postMessageToMainApp,
    requestMainAppOwnerRecovery,
    resetCustomerDisplayToIdle,
    setActiveMainAppTab,
  ])

  // BroadcastChannel message handling
  useEffect(() => {
    const channel = channelRef.current

    if (!channel) return undefined

    channel.onmessage = event => {
      const data = event.data || {}

      if (isCustomerDisplaySourceMessage(data)) return

      const incomingTabId = getCustomerDisplayMessageOwnerTabId(data)

      if (data.windowClose) {
        const registeredOwnerTabId =
          getCustomerDisplayOwnerTabId() || activeMainAppTabIdRef.current

        if (!incomingTabId || incomingTabId !== registeredOwnerTabId) return

        clearCustomerDisplayOwnerTabId(incomingTabId)
        closeCurrentWindowSafely('por solicitud de la app principal')
        return
      }

      if (data.customerDisplayOwnershipRecoveryCandidate) {
        grantMainAppOwnerRecovery(data)
        return
      }

      if (data.customerDisplayActivated && incomingTabId) {
        const ownerChanged = activeMainAppTabIdRef.current !== incomingTabId

        if (!setActiveMainAppTab(incomingTabId)) {
          return
        }

        if (ownerChanged) {
          resetCustomerDisplayToIdle()
        }
      }

      if (data.tabId) {
        if (!activeMainAppTabIdRef.current && data.customerDisplayHeartbeat) {
          setActiveMainAppTab(data.tabId)
        }

        if (
          activeMainAppTabIdRef.current &&
          data.tabId !== activeMainAppTabIdRef.current
        ) {
          return
        }
      } else if (activeMainAppTabIdRef.current) {
        return
      }

      if (data.mainAppTabClosing) {
        requestMainAppOwnerRecovery(
          incomingTabId,
          'por cierre de la app principal'
        )
        return
      }

      clearMainAppOwnerRecovery()
      clearMainAppTabCloseTimeout()
      orphanCloseScheduledRef.current = false
      lastMainAppHeartbeatRef.current = Date.now()

      if (data.customerDisplayHeartbeat) {
        postMessageToMainApp({ customerDisplayHeartbeatAck: true })
        return
      }

      if (data.mainAppTabHidden !== undefined) {
        mainAppTabHiddenRef.current = data.mainAppTabHidden
        if (!data.mainAppTabHidden) {
          lastMainAppHeartbeatRef.current = Date.now()
        }
        return
      }

      // Keep company data updated even while the Customer Display is idle.
      if (data.company) {
        setCompany(data.company)
        setIsChangeCompany(data.isChangeCompany)

        if (data.branchOffice) {
          setBranchOffice(data.branchOffice)
        }
      }

      if (data.branchOffice && !data.company) {
        setBranchOffice(data.branchOffice)
      }

      // Unsupported main routes force sleep mode; ignore timer wake-ups there.
      if (data.isSleepMode !== undefined && mainRouteIsSupportedRef.current) {
        setIsSleepMode(data.isSleepMode)
      }

      if (data.mainRouteIsSupported !== undefined) {
        mainRouteIsSupportedRef.current = data.mainRouteIsSupported

        if (!data.mainRouteIsSupported) {
          resetCustomerDisplayToIdle({ enterSleepMode: true })
          return
        }

        setIsSleepMode(false)
        postMessageToMainApp({ requestCustomerDisplaySync: true })
      }

      if (!mainRouteIsSupportedRef.current) {
        return
      }

      // Handle product list updates
      if (data.listProducts) {
        setListProducts(data.listProducts)
      }

      // Handle shopping cart updates (skip if closing product detail to avoid flicker)
      // Array.isArray guards against isAddedProduct messages (productCart is a single object there)
      // event.data.isProductDetail === undefined guards against Product.jsx handleClose sending
      // stale productCart bundled with isProductDetail:false, which would restore old cart data
      if (
        data.isProductDetail === undefined &&
        Array.isArray(data.productCart) &&
        !isClosingProductDetailRef.current &&
        !data.listProducts
      ) {
        setProductsCart(data.productCart)

        if (data.priceChange === undefined) {
          setPriceChange(false)
        }

        if (
          data.forceCartRoute &&
          data.productCart.length > 0 &&
          !isProductDetailOpenRef.current
        ) {
          history.push(CUSTOMER_DISPLAY_ROUTES.cart)
        }
      }

      // Handle quote data updates
      if (data.quoteData) {
        setQuoteData(data.quoteData)
      }

      // Handle quote loading state
      if (data.isLoadingDetailQuote !== undefined) {
        setIsLoadingDetailQuote(data.isLoadingDetailQuote)
      }

      // Handle discount visibility
      if (data.showDiscount !== undefined) {
        setShowDiscount(data.showDiscount)
      }

      // Handle price changes
      if (data.priceChange !== undefined) {
        setPriceChange(data.priceChange)
      }

      // Handle loading states
      if (data.isLoading !== undefined) {
        setIsLoadingProductSearch(data.isLoading)
      }

      // Handle financial totals
      if (data.subtotal !== undefined) {
        setSubtotal(data.subtotal)
      }
      if (data.itbms !== undefined) {
        setItbms(data.itbms)
      }
      if (data.total !== undefined) {
        setTotal(data.total)
      }

      // NAVIGATION LOGIC - Route based on message type

      // Navigate to quote detail
      if (data.isDetailQuote) {
        history.push(CUSTOMER_DISPLAY_ROUTES.quote)
      }

      // Reset the view when entering a new supported flow.
      if (data.forceIdleMode) {
        resetCustomerDisplayToIdle()
        return
      }

      // An explicit search action takes precedence over the current cart view.
      if (data.isSearchProducts) {
        history.push(CUSTOMER_DISPLAY_ROUTES.search)
      }

      // Show product detail modal (guard against ghost clicks right after closing)
      if (data.isProductDetail && !isClosingProductDetailRef.current) {
        setProductDetail(data.productDetail)
        handleSetIsProductDetailOpen(true)
      }

      // Close product detail modal when main app closes it
      if (data.isProductDetail === false) {
        handleSetIsProductDetailOpen(false)
      }
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.close()
        channelRef.current = null
      }
    }
  }, [
    clearMainAppOwnerRecovery,
    clearMainAppTabCloseTimeout,
    grantMainAppOwnerRecovery,
    handleSetIsProductDetailOpen,
    history,
    postMessageToMainApp,
    requestMainAppOwnerRecovery,
    resetCustomerDisplayToIdle,
    setActiveMainAppTab,
  ])

  // Computed values
  const isValidQuoteData = quoteData && Object.keys(quoteData).length > 0

  const setTableData = products => {
    if (!products) return []
    return products?.map(product => ({
      referencia: product?.Sku,
      descripción: product?.ProductName,
      cantidad: product?.Quantity,
      precio_unitario: getQuoteUnitPrice(product, showDiscount),
      descuento: product?.DiscountAmount,
      precio_total: product?.Price * product?.Quantity,
      image: product?.DefaultProductPicture || product?.Images?.[0]?.URL,
    }))
  }

  const productsData = isValidQuoteData ? setTableData(quoteData?.Lines) : []

  const value = {
    // Data states
    listProducts,
    isLoadingProductSearch,
    productsCart,
    productDetail,
    quoteData,
    productsData,
    isLoadingDetailQuote,
    subtotal,
    itbms,
    total,
    company,
    branchOffice,
    isChangeCompany,
    showDiscount,
    priceChange,
    isProductDetailOpen,
    isSleepMode,

    // Actions used by child components
    setIsProductDetailOpen: handleSetIsProductDetailOpen,
    postToMainApp: postMessageToMainApp,
  }

  return (
    <CustomerDisplayContext.Provider value={value}>
      {children}
    </CustomerDisplayContext.Provider>
  )
}
